import { Brand, SocialProfile } from "@features/events/types";
import { validateUrl } from "@shared/lib";

export interface ValidationErrors {
  [key: string]: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationErrors;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationFunction = (key: string, options?: any) => string;

/**
 * Validates required_with fields for multilingual content
 * If any language has content, all selected languages must have content
 */
export function validateRequiredWithMultilingual(
  field: Record<string, string> | undefined,
  selectedLanguages: string[],
  fieldName: string,
  errors: ValidationErrors,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string,
  translationKey: string
): void {
  if (!field) return;

  // Check if any language has content
  const hasAnyContent = Object.values(field).some((value) => value?.trim());

  if (hasAnyContent && selectedLanguages.length > 0) {
    // If any language has content, all selected languages must have content
    const missingLanguages = selectedLanguages.filter(
      (lang) => !field[lang]?.trim()
    );
    if (missingLanguages.length > 0) {
      errors[fieldName] = t("common.requiredWithField", {
        field_name: t(translationKey),
        missing_languages: missingLanguages.join(", "),
        defaultValue: `${t(
          translationKey
        )} is missing content for: ${missingLanguages.join(", ")}`,
      });
    }
  }
}

/**
 * Validates social media items that are required when the parent social array exists
 */
function validateSocialItems(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  socialItems: any[] | undefined,
  errors: ValidationErrors,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string
): void {
  if (socialItems && socialItems.length > 0) {
    socialItems.forEach((socialItem, index) => {
      if (!socialItem.url || !socialItem.url.trim()) {
        errors[`social.${index}.url`] = t("common.requiredField", {
          field_name: `Social ${index + 1} URL`,
        });
      }
      if (!socialItem.social || !socialItem.social.trim()) {
        errors[`social.${index}.social`] = t("common.requiredField", {
          field_name: `Social ${index + 1} Platform`,
        });
      }
    });
  }
}

/**
 * Clears or updates validation errors for a specific field in real-time
 * Used for providing immediate feedback as users edit fields
 * @param field - The field name being updated
 * @param value - The new value of the field
 * @param brandData - The complete brand data (for cross-field validation)
 * @param currentErrors - Current validation errors object
 * @param selectedLanguages - Array of selected languages for multilingual validation
 * @param t - Translation function
 * @returns Updated errors object
 */
export function clearBrandFieldError(
  field: string,
  value: unknown,
  currentErrors: ValidationErrors,
  selectedLanguages: string[],
  t: TranslationFunction
): ValidationErrors {
  const newErrors = { ...currentErrors };

  switch (field) {
    case "name":
    case "headline":
    case "description": {
      const multilingualValue = value as Record<string, string> | undefined;
      const translationKey = `brandProfile.overview.${field}`;
      
      if (multilingualValue) {
        // Check if all selected languages have content
        if (selectedLanguages.length > 0) {
          const hasAllLanguages = selectedLanguages.every((lang) =>
            multilingualValue[lang]?.trim()
          );
          if (hasAllLanguages) {
            delete newErrors[field];
          } else {
            // Check for required_with validation (partial content)
            const hasAnyContent = Object.values(multilingualValue).some((v) => v?.trim());
            if (hasAnyContent) {
              const missingLanguages = selectedLanguages.filter(
                (lang) => !multilingualValue[lang]?.trim()
              );
              if (missingLanguages.length > 0) {
                newErrors[field] = t("common.requiredWithField", {
                  field_name: t(translationKey),
                  missing_languages: missingLanguages.join(", "),
                  defaultValue: `${t(translationKey)} is missing content for: ${missingLanguages.join(", ")}`,
                });
              }
            }
          }
        } else {
          // No selected languages - just check if any language has content
          if (Object.values(multilingualValue).some((v) => v?.trim())) {
            delete newErrors[field];
          }
        }
      }
      break;
    }

    case "customButtonLabel": {
      const multilingualValue = value as Record<string, string> | undefined;
      // customButtonLabel is optional, but if any language has content, validate all
      if (multilingualValue && selectedLanguages.length > 0) {
        const hasAnyContent = Object.values(multilingualValue).some((v) => v?.trim());
        if (hasAnyContent) {
          const missingLanguages = selectedLanguages.filter(
            (lang) => !multilingualValue[lang]?.trim()
          );
          if (missingLanguages.length > 0) {
            newErrors[field] = t("common.requiredWithField", {
              field_name: t("brandProfile.branding.customButtonLabel"),
              missing_languages: missingLanguages.join(", "),
              defaultValue: `Custom button label is missing content for: ${missingLanguages.join(", ")}`,
            });
          } else {
            delete newErrors[field];
          }
        } else {
          delete newErrors[field];
        }
      } else {
        delete newErrors[field];
      }
      break;
    }

    case "contactEmail": {
      const emailValue = value as string | undefined;
      if (emailValue?.trim()) {
        delete newErrors.contactEmail;
      }
      break;
    }

    case "contactPhone": {
      const phoneValue = value as string | undefined;
      if (!phoneValue || !phoneValue.trim()) {
        // Phone is optional - clear any errors if empty
        delete newErrors.contactPhone;
      } else {
        // Validate format: only digits
        const phoneRegex = /^\d+$/;
        if (phoneRegex.test(phoneValue)) {
          delete newErrors.contactPhone;
        } else {
          newErrors.contactPhone = t("brandProfile.contact.invalidPhone", {
            defaultValue: "Phone number must contain only digits",
          });
        }
      }
      break;
    }

    case "contactPhoneCountryCode": {
      const countryCodeValue = value as string | undefined;
      if (!countryCodeValue || !countryCodeValue.trim()) {
        // Country code is optional - clear any errors if empty
        delete newErrors.contactPhoneCountryCode;
      } else {
        // Validate format: + followed by digits
        const countryCodeRegex = /^\+\d+$/;
        if (countryCodeRegex.test(countryCodeValue)) {
          delete newErrors.contactPhoneCountryCode;
        } else {
          newErrors.contactPhoneCountryCode = t("brandProfile.contact.invalidCountryCode", {
            defaultValue: "Country code must start with + followed by digits",
          });
        }
      }
      break;
    }

    case "billingEmail": {
      const emailValue = value as string | undefined;
      if (emailValue !== undefined) {
        if (emailValue.trim()) {
          // Validate comma-separated emails
          const emails = emailValue.split(',').map(email => email.trim());
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const invalidEmails = emails.filter(email => email && !emailRegex.test(email));
          
          if (invalidEmails.length === 0) {
            delete newErrors.billingEmail;
          } else {
            newErrors.billingEmail = t("brandProfile.billing.invalidEmails", {
              emails: invalidEmails.join(', '),
              defaultValue: `Invalid email addresses: ${invalidEmails.join(', ')}`,
            });
          }
        }
        // Note: Don't clear required error here - that's checked on save
      }
      break;
    }

    case "slug": {
      const slugValue = value as string | undefined;
      if (slugValue?.trim()) {
        delete newErrors.slug;
      }
      break;
    }

    case "languageCodes": {
      const codesValue = value as string[] | undefined;
      if (codesValue && codesValue.length > 0) {
        delete newErrors.languageCodes;
      }
      break;
    }

    case "visibility": {
      if (value !== undefined && value !== null) {
        delete newErrors.visibility;
      }
      break;
    }

    case "geoAddress": {
      const geoValue = value as Brand["geoAddress"] | undefined;
      if (geoValue?.googlePlaceId) {
        delete newErrors.geoAddress;
      }
      break;
    }

    case "website": {
      const websiteValue = value as string | undefined;
      // Website is REQUIRED - only clear error if valid URL is provided
      if (websiteValue && websiteValue.trim()) {
        const urlValidation = validateUrl(websiteValue, {
          requireHttps: true,
          allowEmpty: false,
        });
        if (urlValidation.isValid) {
          delete newErrors.website;
        } else if (urlValidation.errorCode) {
          newErrors.website = t(`common.website_field.${urlValidation.errorCode}`, {
            defaultValue: t("common.website_field.invalid_url", {
              defaultValue: "Invalid URL format",
            }),
          });
        }
      }
      // Don't clear required error if empty - that's checked on save
      break;
    }

    case "customButtonLink": {
      const linkValue = value as string | undefined;
      if (!linkValue || !linkValue.trim()) {
        delete newErrors.customButtonLink;
      } else {
        const urlValidation = validateUrl(linkValue, {
          requireHttps: true,
          allowEmpty: true,
        });
        if (urlValidation.isValid) {
          delete newErrors.customButtonLink;
        } else if (urlValidation.errorCode) {
          newErrors.customButtonLink = t(`common.website_field.${urlValidation.errorCode}`, {
            defaultValue: t("common.website_field.invalid_url", {
              defaultValue: "Invalid URL format",
            }),
          });
        }
      }
      break;
    }

    case "social": {
      const socialValue = value as SocialProfile[] | undefined;
      // Clear existing social errors
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith("social.")) {
          delete newErrors[key];
        }
      });

      // Validate each social item if array exists
      if (socialValue && socialValue.length > 0) {
        socialValue.forEach((socialItem, index) => {
          if (!socialItem.url || !socialItem.url.trim()) {
            newErrors[`social.${index}.url`] = t("common.requiredField", {
              field_name: `Social ${index + 1} URL`,
            });
          }
          if (!socialItem.social || !socialItem.social.trim()) {
            newErrors[`social.${index}.social`] = t("common.requiredField", {
              field_name: `Social ${index + 1} Platform`,
            });
          }
        });
      }
      break;
    }
  }

  return newErrors;
}

/**
 * Validates required fields for creating/editing ANY brand
 * (Used in BrandDetails.tsx for brand creation/editing)
 * @param brandData - The brand data to validate
 * @param t - Translation function
 * @param selectedLanguages - Array of selected languages for multilingual validation
 * @returns ValidationResult with isValid boolean and errors object
 */
export function validateBrandRequiredFields(
  brandData: Partial<Brand>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string,
  selectedLanguages: string[] = []
): ValidationResult {
  const errors: ValidationErrors = {};

  // Name validation - check if all selected languages have content
  if (selectedLanguages.length > 0) {
    const hasAllLanguages = selectedLanguages.every((lang) =>
      brandData.name?.[lang]?.trim()
    );
    if (!brandData.name || !hasAllLanguages) {
      errors["name"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.name"),
      });
    }
  } else {
    // Fallback: check if any language has content
    if (
      !brandData.name ||
      !Object.values(brandData.name).some((value) => value?.trim())
    ) {
      errors["name"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.name"),
      });
    }
  }

  // Headline validation - check if all selected languages have content
  if (selectedLanguages.length > 0) {
    const hasAllLanguages = selectedLanguages.every((lang) =>
      brandData.headline?.[lang]?.trim()
    );
    if (!brandData.headline || !hasAllLanguages) {
      errors["headline"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.headline"),
      });
    }
  } else {
    // Fallback: check if any language has content
    if (
      !brandData.headline ||
      !Object.values(brandData.headline).some((value) => value?.trim())
    ) {
      errors["headline"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.headline"),
      });
    }
  }

  // Description validation - check if all selected languages have content
  if (selectedLanguages.length > 0) {
    const hasAllLanguages = selectedLanguages.every((lang) =>
      brandData.description?.[lang]?.trim()
    );
    if (!brandData.description || !hasAllLanguages) {
      errors["description"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.description"),
      });
    }
  } else {
    // Fallback: check if any language has content
    if (
      !brandData.description ||
      !Object.values(brandData.description).some((value) => value?.trim())
    ) {
      errors["description"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.description"),
      });
    }
  }

  // Slug validation
  if (!brandData.slug || !brandData.slug.trim()) {
    errors["slug"] = t("common.requiredField", {
      field_name: t("brandProfile.overview.slug"),
    });
  }

  // Location validation - REQUIRED (geoAddress.googlePlaceId)
  if (!brandData.geoAddress || !brandData.geoAddress.googlePlaceId) {
    errors["geoAddress"] = t("common.requiredField", {
      field_name: t("brandProfile.location.title"),
    });
  }

  // Contact email is OPTIONAL for brand creation

  // Language codes validation
  if (!brandData.languageCodes || brandData.languageCodes.length === 0) {
    errors["languageCodes"] = t("common.requiredField", {
      field_name: t("brandProfile.languages.title"),
    });
  }

  // Visibility validation
  if (brandData.visibility === undefined || brandData.visibility === null) {
    errors["visibility"] = t("common.requiredField", {
      field_name: t("brandProfile.status.visibility"),
    });
  }

  // Required_with validations (if any language has content, all selected languages must have content)
  validateRequiredWithMultilingual(
    brandData.name,
    selectedLanguages,
    "name",
    errors,
    t,
    "brandProfile.overview.name"
  );

  validateRequiredWithMultilingual(
    brandData.headline,
    selectedLanguages,
    "headline",
    errors,
    t,
    "brandProfile.overview.headline"
  );

  validateRequiredWithMultilingual(
    brandData.description,
    selectedLanguages,
    "description",
    errors,
    t,
    "brandProfile.overview.description"
  );

  validateRequiredWithMultilingual(
    brandData.customButtonLabel,
    selectedLanguages,
    "customButtonLabel",
    errors,
    t,
    "brandProfile.branding.customButtonLabel"
  );

  // Social media validation (required per item when social array exists)
  validateSocialItems(brandData.social, errors, t);

  // Website URL validation (optional field, but validate format if provided)
  if (brandData.website && brandData.website.trim()) {
    const urlValidation = validateUrl(brandData.website, {
      requireHttps: true,
      allowEmpty: false,
    });
    if (!urlValidation.isValid && urlValidation.errorCode) {
      errors["website"] = t(`common.website_field.${urlValidation.errorCode}`, {
        defaultValue: t("common.website_field.invalid_url", {
          defaultValue: "Invalid URL format",
        }),
      });
    }
  }

  // Custom button link URL validation (optional field, but validate format if provided)
  if (brandData.customButtonLink && brandData.customButtonLink.trim()) {
    const urlValidation = validateUrl(brandData.customButtonLink, {
      requireHttps: true,
      allowEmpty: false,
    });
    if (!urlValidation.isValid && urlValidation.errorCode) {
      errors["customButtonLink"] = t(`common.website_field.${urlValidation.errorCode}`, {
        defaultValue: t("common.website_field.invalid_url", {
          defaultValue: "Invalid URL format",
        }),
      });
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validates required fields for MY brand profile
 * (Used in BrandProfile.tsx for editing the user's own brand)
 * @param brandData - The brand data to validate
 * @param t - Translation function
 * @param selectedLanguages - Array of selected languages for multilingual validation
 * @returns ValidationResult with isValid boolean and errors object
 */
export function validateMyBrandRequiredFields(
  brandData: Partial<Brand>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string,
  selectedLanguages: string[] = []
): ValidationResult {
  const errors: ValidationErrors = {};

  // Name validation - check if all selected languages have content
  if (selectedLanguages.length > 0) {
    const hasAllLanguages = selectedLanguages.every((lang) =>
      brandData.name?.[lang]?.trim()
    );
    if (!brandData.name || !hasAllLanguages) {
      errors["name"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.name"),
      });
    }
  } else {
    // Fallback: check if any language has content
    if (
      !brandData.name ||
      !Object.values(brandData.name).some((value) => value?.trim())
    ) {
      errors["name"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.name"),
      });
    }
  }

  // Headline validation - check if all selected languages have content
  if (selectedLanguages.length > 0) {
    const hasAllLanguages = selectedLanguages.every((lang) =>
      brandData.headline?.[lang]?.trim()
    );
    if (!brandData.headline || !hasAllLanguages) {
      errors["headline"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.headline"),
      });
    }
  } else {
    // Fallback: check if any language has content
    if (
      !brandData.headline ||
      !Object.values(brandData.headline).some((value) => value?.trim())
    ) {
      errors["headline"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.headline"),
      });
    }
  }

  // Description validation - check if all selected languages have content
  if (selectedLanguages.length > 0) {
    const hasAllLanguages = selectedLanguages.every((lang) =>
      brandData.description?.[lang]?.trim()
    );
    if (!brandData.description || !hasAllLanguages) {
      errors["description"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.description"),
      });
    }
  } else {
    // Fallback: check if any language has content
    if (
      !brandData.description ||
      !Object.values(brandData.description).some((value) => value?.trim())
    ) {
      errors["description"] = t("common.requiredField", {
        field_name: t("brandProfile.overview.description"),
      });
    }
  }

  // Slug validation
  if (!brandData.slug || !brandData.slug.trim()) {
    errors["slug"] = t("common.requiredField", {
      field_name: t("brandProfile.overview.slug"),
    });
  }

  // Location validation - REQUIRED (geoAddress.googlePlaceId)
  if (!brandData.geoAddress || !brandData.geoAddress.googlePlaceId) {
    errors["geoAddress"] = t("common.requiredField", {
      field_name: t("brandProfile.location.title"),
    });
  }

  // Contact email validation - REQUIRED for my brand
  if (!brandData.contactEmail || !brandData.contactEmail.trim()) {
    errors["contactEmail"] = t("common.requiredField", {
      field_name: t("brandProfile.contact.email"),
    });
  }

  // Phone validation - OPTIONAL but validate format if provided
  // contactPhone: only digits, contactPhoneCountryCode: + followed by digits
  if (brandData.contactPhone && brandData.contactPhone.trim()) {
    const phoneRegex = /^\d+$/;
    if (!phoneRegex.test(brandData.contactPhone)) {
      errors["contactPhone"] = t("brandProfile.contact.invalidPhone", {
        defaultValue: "Phone number must contain only digits",
      });
    }
  }
  if (brandData.contactPhoneCountryCode && brandData.contactPhoneCountryCode.trim()) {
    const countryCodeRegex = /^\+\d+$/;
    if (!countryCodeRegex.test(brandData.contactPhoneCountryCode)) {
      errors["contactPhoneCountryCode"] = t("brandProfile.contact.invalidCountryCode", {
        defaultValue: "Country code must start with + followed by digits",
      });
    }
  }

  // Billing email validation - REQUIRED for my brand (API: UpdateMeRequest)
  if (!brandData.billingEmail || !brandData.billingEmail.trim()) {
    errors["billingEmail"] = t("common.requiredField", {
      field_name: t("brandProfile.billing.email"),
    });
  } else {
    // Validate comma-separated email format
    const emails = brandData.billingEmail.split(',').map(email => email.trim());
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(email => email && !emailRegex.test(email));
    
    if (invalidEmails.length > 0) {
      errors["billingEmail"] = t("brandProfile.billing.invalidEmails", {
        emails: invalidEmails.join(', '),
        defaultValue: `Invalid email addresses: ${invalidEmails.join(', ')}`,
      });
    }
  }

  // Language codes validation
  if (!brandData.languageCodes || brandData.languageCodes.length === 0) {
    errors["languageCodes"] = t("common.requiredField", {
      field_name: t("brandProfile.languages.title"),
    });
  }

  // Visibility validation
  if (brandData.visibility === undefined || brandData.visibility === null) {
    errors["visibility"] = t("common.requiredField", {
      field_name: t("brandProfile.status.visibility"),
    });
  }

  // Required_with validations (if any language has content, all selected languages must have content)
  validateRequiredWithMultilingual(
    brandData.name,
    selectedLanguages,
    "name",
    errors,
    t,
    "brandProfile.overview.name"
  );

  validateRequiredWithMultilingual(
    brandData.headline,
    selectedLanguages,
    "headline",
    errors,
    t,
    "brandProfile.overview.headline"
  );

  validateRequiredWithMultilingual(
    brandData.description,
    selectedLanguages,
    "description",
    errors,
    t,
    "brandProfile.overview.description"
  );

  validateRequiredWithMultilingual(
    brandData.customButtonLabel,
    selectedLanguages,
    "customButtonLabel",
    errors,
    t,
    "brandProfile.branding.customButtonLabel"
  );

  // Social media validation - OPTIONAL (social profiles are not mandatory)
  // Only validate format if items exist, but don't require the array itself

  // Website URL validation - REQUIRED for my brand
  if (!brandData.website || !brandData.website.trim()) {
    errors["website"] = t("common.requiredField", {
      field_name: t("brandProfile.contact.website"),
    });
  } else {
    const urlValidation = validateUrl(brandData.website, {
      requireHttps: true,
      allowEmpty: false,
    });
    if (!urlValidation.isValid && urlValidation.errorCode) {
      errors["website"] = t(`common.website_field.${urlValidation.errorCode}`, {
        defaultValue: t("common.website_field.invalid_url", {
          defaultValue: "Invalid URL format",
        }),
      });
    }
  }

  // Custom button link URL validation (optional field, but validate format if provided)
  if (brandData.customButtonLink && brandData.customButtonLink.trim()) {
    const urlValidation = validateUrl(brandData.customButtonLink, {
      requireHttps: true,
      allowEmpty: false,
    });
    if (!urlValidation.isValid && urlValidation.errorCode) {
      errors["customButtonLink"] = t(`common.website_field.${urlValidation.errorCode}`, {
        defaultValue: t("common.website_field.invalid_url", {
          defaultValue: "Invalid URL format",
        }),
      });
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

